import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function RFQQuoteForm() {
  const { id } = useParams();
  const [rfq, setRfq] = useState(null);
  const [items, setItems] = useState([]);
  const [leadTime, setLeadTime] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/vendor/rfqs/${id}`).then((r) => {
    setRfq(r.data);
    if (r.data.my_quote) {
      setItems(r.data.my_quote.items);
      setLeadTime(r.data.my_quote.lead_time_days || "");
      setNotes(r.data.my_quote.notes || "");
    } else {
      setItems(r.data.items.map((i) => ({ description: i.description, qty: i.qty, unit_price: 0 })));
    }
  });
  useEffect(() => { load(); }, [id]);

  if (!rfq) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const updatePrice = (i, value) => {
    const next = [...items];
    next[i].unit_price = Number(value);
    setItems(next);
  };

  const total = items.reduce((s, i) => s + i.qty * i.unit_price, 0);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/vendor/rfqs/${id}/quote`, { items, lead_time_days: leadTime ? Number(leadTime) : null, notes });
      toast.success("Quote submitted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit quote");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="rfq-quote-page">
      <Link to="/vendor/rfqs" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to RFQs</Link>
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight mono">{rfq.rfq_number}</h1>
        <StatusBadge status={rfq.status} />
      </div>
      <p className="text-slate-500 text-sm mt-1">{rfq.title}</p>

      <div className="mt-7">
        <label className="text-[13px] font-semibold text-slate-700">Your Pricing</label>
        <div className="mt-2 space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-6 text-[13px]">{it.description} <span className="text-slate-400">× {it.qty}</span></div>
              <input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updatePrice(i, e.target.value)} disabled={rfq.status !== "open"} data-testid={`quote-price-${i}`} className="col-span-3 h-9 px-2.5 rounded-lg border border-slate-200 text-[13px] disabled:bg-slate-50" placeholder="Unit price" />
              <div className="col-span-3 text-right mono text-[13px] font-medium">{fmt(it.qty * it.unit_price)}</div>
            </div>
          ))}
        </div>
        <div className="text-right mt-3 text-[14px] font-bold">Total: {fmt(total)}</div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[13px] font-medium text-slate-700">Lead Time (days)</label>
          <input type="number" value={leadTime} onChange={(e) => setLeadTime(e.target.value)} disabled={rfq.status !== "open"} data-testid="quote-leadtime-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" />
        </div>
        <div>
          <label className="text-[13px] font-medium text-slate-700">Notes</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} disabled={rfq.status !== "open"} data-testid="quote-notes-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" />
        </div>
      </div>

      {rfq.status === "open" ? (
        <button disabled={saving} onClick={submit} data-testid="submit-quote-button" className="mt-7 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">
          {saving ? "Submitting…" : rfq.my_quote ? "Update Quote" : "Submit Quote"}
        </button>
      ) : (
        <div className="mt-7 text-[13px] text-slate-500">This RFQ is closed for new quotes.</div>
      )}
    </div>
  );
}
